'use client'

import React from 'react'
import type { Vacancy } from '@/payload-types'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import RichText from '@/components/RichText'
import { VacancyTagList } from '@/components/VacancyTag'
import { useApplicationModalStore } from '@/stores/applicationModalStore'

interface VacancyPreviewCardProps {
  vacancy: Vacancy
  salaryDisplay: string | null
  locale?: string
}

export function VacancyPreviewCard({
  vacancy,
  salaryDisplay,
  locale = 'uk',
}: VacancyPreviewCardProps) {
  const { openModal } = useApplicationModalStore()

  const joinText = locale === 'uk' ? 'Приєднатися' : 'Apply Now'
  const detailsText = locale === 'uk' ? 'Детальніше' : 'Details'
  const href = `/${locale}/vacancies/${vacancy.slug}`

  return (
    <div className="relative group block border-2 border-border rounded-lg p-4 md:p-5 bg-card hover:border-primary hover:bg-primary/5 transition-all duration-300 bg-zinc-900 cursor-pointer">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-6">
        {/* Content Section */}
        <div className="flex-1 min-w-0">
          <Link href={href} className="no-underline">
            <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-3 group-hover:text-primary transition-colors">
              {vacancy.title}
            </h3>
          </Link>

          {/* Salary - show on mobile inline with title area */}
          {salaryDisplay && (
            <div className="md:hidden font-semibold text-primary text-base mb-3">
              {salaryDisplay}
            </div>
          )}

          {vacancy.shortDescription && (
            <div className="prose prose-sm md:prose prose-invert max-w-none mb-3 md:mb-4 line-clamp-2 md:line-clamp-none text-muted-foreground">
              <RichText data={vacancy.shortDescription} enableGutter={false} />
            </div>
          )}

          <VacancyTagList tags={vacancy.tags} className="flex flex-wrap gap-1.5 md:gap-2" />
        </div>

        {/* Actions Section */}
        <div className="flex flex-col gap-2 md:gap-3 md:justify-between md:items-end">
          {/* Salary - desktop only */}
          {salaryDisplay && (
            <div className="hidden md:block text-center font-semibold text-lg mb-1">
              {salaryDisplay}
            </div>
          )}

          {/* Buttons - horizontal on mobile, vertical on desktop */}
          <div className="flex flex-row md:flex-col gap-2 md:gap-3 md:min-w-[140px]">
            <Button
              size="sm"
              variant="outline"
              className="rounded-full md:rounded-lg flex-1 md:flex-initial border-2 border-primary hover:bg-primary hover:text-primary-foreground transition-colors text-sm md:text-base md:h-11 md:px-8"
              onClick={() => openModal('civil', vacancy.slug)}
            >
              {joinText}
            </Button>
            <Button
              asChild
              size="sm"
              variant="outline"
              className="rounded-full md:rounded-lg flex-1 md:flex-initial text-sm md:text-base md:h-11 md:px-8"
            >
              <Link href={`/${locale}/vacancies/${vacancy.slug}`}>{detailsText}</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
